import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { de } from "@ainkauf/core";
import { alsAppInstalliert, pushAbmelden, pushAnmelden, pushZustand, type PushZustand } from "@/lib/push";

/**
 * Benachrichtigungen an oder aus, für dieses Gerät.
 *
 * Der Zustand kommt vom Browser und nicht aus der Datenbank: Was zählt, ist
 * ob dieser Browser gerade angemeldet ist. Eine Zeile in einer Tabelle kann
 * das nur behaupten.
 */
export function PushSchalter() {
  const [zustand, setZustand] = useState<PushZustand | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    void pushZustand().then(setZustand);
  }, []);

  async function umschalten() {
    setLaeuft(true);
    try {
      setZustand(zustand === "an" ? await pushAbmelden() : await pushAnmelden());
    } finally {
      setLaeuft(false);
    }
  }

  if (zustand === null) return null;

  const an = zustand === "an";
  const schaltbar = zustand === "an" || zustand === "aus";

  // Auf dem iPhone liefert Safari Push erst, wenn die App auf dem
  // Startbildschirm liegt. Das steht hier, weil es sonst so aussieht, als
  // wäre die App kaputt.
  const hinweis =
    zustand === "verweigert"
      ? de.settings.pushDenied
      : zustand === "nicht-eingerichtet"
        ? de.settings.pushNotConfigured
        : zustand === "nicht-unterstützt"
          ? alsAppInstalliert()
            ? de.settings.pushUnsupported
            : de.settings.pushInstallFirst
          : de.settings.pushHint;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-lg">{de.settings.pushSection}</h2>

      <div className="karte kante flex items-center gap-3 py-4 pl-4 pr-3" style={{ ["--kante-farbe" as string]: an ? "var(--herb)" : "var(--paper-3)" }}>
        <span
          aria-hidden
          className="inline-grid size-11 shrink-0 place-items-center rounded-full"
          style={{ background: an ? "var(--herb-soft)" : "var(--paper-2)", color: an ? "var(--herb)" : "var(--ink-3)" }}
        >
          {an ? <Bell size={20} strokeWidth={2} /> : <BellOff size={20} strokeWidth={2} />}
        </span>

        <div className="min-w-0 flex-1">
          <p className="font-semibold text-ink" data-testid="push-state">
            {an ? de.settings.pushOn : de.settings.pushOff}
          </p>
          <p className="text-sm text-ink-2">{hinweis}</p>
        </div>

        {schaltbar && (
          <button
            type="button"
            onClick={() => void umschalten()}
            disabled={laeuft}
            data-testid="push-toggle"
            className={`inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-4 text-sm font-semibold disabled:opacity-60 ${
              an ? "bg-paper-2 text-ink-2" : "bg-herb text-white"
            }`}
          >
            {laeuft && <Loader2 size={15} className="animate-spin" aria-hidden />}
            {an ? de.settings.pushDisable : de.settings.pushEnable}
          </button>
        )}
      </div>
    </section>
  );
}
